import { ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { ErrorState } from '@/components/error-state';
import { Button } from '@/components/health-ui';
import { useAuth } from '@/lib/auth-guards';
import { isProviderAccount } from '@/lib/human-account';
import { useProviderCapabilitiesQuery } from '@/lib/provider-capabilities';

type ProviderCapabilitiesGateProps = {
  children: ReactNode;
};

/**
 * Resolves the served Provider Capability set before any route loader mounts
 * (ADR 0275). The set is no longer carried in the access token, so the reply is
 * its only source: an absent, failed, or Human-Account-Type-inconsistent reply
 * denies the workspace rather than rendering it with silently zero capabilities.
 *
 * Staff Accounts are admitted without a request — Staff Capabilities come from
 * Staff Access Profile Assignments, never from this surface.
 */
export function ProviderCapabilitiesGate({
  children,
}: Readonly<ProviderCapabilitiesGateProps>): React.JSX.Element {
  const { humanAccountTypes, logout, user } = useAuth();
  const isProvider = isProviderAccount(humanAccountTypes);
  const subject = user?.id ?? '';
  const capabilities = useProviderCapabilitiesQuery(
    isProvider && subject.length > 0,
  );

  if (!isProvider) {
    return <>{children}</>;
  }

  const malformedReply =
    capabilities.isSuccess &&
    (capabilities.data.humanAccountType !== humanAccountTypes[0] ||
      !Array.isArray(capabilities.data.capabilities));

  // Fail closed only while nothing has resolved. Once the set is in hand, a
  // failed background refetch of a compiled constant is not a revocation —
  // unmounting here would take the router and every in-flight note draft with
  // it. The sibling ApplicationAccessGate may unmount on any error because it
  // polls on an interval as a deliberate revocation dead-man's switch; this set
  // has no such semantic.
  const unresolvable =
    subject.length === 0 ||
    capabilities.isPaused ||
    (capabilities.isError && !capabilities.data);

  if (unresolvable || malformedReply) {
    return (
      <main className="min-h-screen bg-background text-foreground">
        <ErrorState
          tone="danger"
          icon={ShieldAlert}
          headline="Unable to verify provider capabilities"
          description="Your clinical workspace remains locked because your current capabilities could not be loaded. Try again in a moment."
          actions={[
            <Button
              key="retry-capabilities"
              onClick={() => void capabilities.refetch()}
              disabled={capabilities.isFetching}
            >
              Check capabilities again
            </Button>,
            <Button
              key="sign-out"
              variant="outline"
              onClick={() => void logout()}
            >
              Sign out
            </Button>,
          ]}
          compact={false}
        />
      </main>
    );
  }

  if (capabilities.isPending) {
    return (
      <main
        className="flex min-h-screen items-center justify-center bg-background px-6 text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        Checking provider capabilities…
      </main>
    );
  }

  return <>{children}</>;
}
